/**
 * Typed application errors.
 *
 * Every error carries a stable machine code so the three frontends can react
 * (and translate) without string-matching messages. Messages are Vietnamese
 * for internal apps; customer-facing surfaces translate by code instead.
 */

export const ERROR_STATUS = {
  VALIDATION_FAILED: 400,
  MALFORMED_REQUEST: 400,
  UNAUTHENTICATED: 401,
  SESSION_EXPIRED: 401,
  MFA_REQUIRED: 401,
  FORBIDDEN: 403,
  PERMISSION_DENIED: 403,
  TENANT_MISMATCH: 403,
  BRANCH_MISMATCH: 403,
  DEVICE_NOT_AUTHORIZED: 403,
  REASON_REQUIRED: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  VERSION_CONFLICT: 409,
  DUPLICATE_REQUEST: 409,
  INVALID_STATE: 409,
  ALREADY_CLAIMED: 409,
  GONE: 410,
  QR_EXPIRED: 410,
  SESSION_CLOSED: 410,
  PAYLOAD_TOO_LARGE: 413,
  RATE_LIMITED: 429,
  INTERNAL_ERROR: 500,
  NOT_IMPLEMENTED: 501,
  UPSTREAM_FAILED: 502,
  UNAVAILABLE: 503,
} as const;

export type ErrorCode = keyof typeof ERROR_STATUS;

export interface AppErrorOptions {
  readonly details?: Record<string, unknown>;
  readonly cause?: unknown;
  /** Business-code key so the customer app can show a translated message. */
  readonly messageCode?: string;
  readonly retryable?: boolean;
}

export class AppError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly details?: Record<string, unknown>;
  readonly messageCode?: string;
  readonly retryable: boolean;

  constructor(code: ErrorCode, message: string, options: AppErrorOptions = {}) {
    super(message, options.cause !== undefined ? { cause: options.cause } : undefined);
    this.name = 'AppError';
    this.code = code;
    this.status = ERROR_STATUS[code];
    this.details = options.details;
    this.messageCode = options.messageCode;
    this.retryable = options.retryable ?? (code === 'UNAVAILABLE' || code === 'UPSTREAM_FAILED');
  }

  toJSON(): Record<string, unknown> {
    return {
      error: {
        code: this.code,
        message: this.message,
        messageCode: this.messageCode,
        details: this.details,
        retryable: this.retryable,
      },
    };
  }
}

export const errors = {
  validation: (message: string, details?: Record<string, unknown>): AppError =>
    new AppError('VALIDATION_FAILED', message, { details }),
  unauthenticated: (message = 'Chưa đăng nhập hoặc phiên không hợp lệ.'): AppError =>
    new AppError('UNAUTHENTICATED', message),
  forbidden: (message = 'Bạn không có quyền thực hiện thao tác này.', details?: Record<string, unknown>): AppError =>
    new AppError('PERMISSION_DENIED', message, { details }),
  notFound: (resource: string): AppError => new AppError('NOT_FOUND', `Không tìm thấy ${resource}.`),
  conflict: (message: string, details?: Record<string, unknown>): AppError =>
    new AppError('CONFLICT', message, { details }),
  versionConflict: (expected: number, actual: number): AppError =>
    new AppError('VERSION_CONFLICT', 'Dữ liệu đã được người khác thay đổi. Vui lòng tải lại.', {
      details: { expected, actual },
    }),
  invalidState: (message: string, details?: Record<string, unknown>): AppError =>
    new AppError('INVALID_STATE', message, { details }),
  duplicate: (message = 'Yêu cầu đã được xử lý trước đó.'): AppError => new AppError('DUPLICATE_REQUEST', message),
  rateLimited: (retryAfterSeconds: number): AppError =>
    new AppError('RATE_LIMITED', 'Bạn thao tác quá nhanh. Vui lòng thử lại sau.', {
      details: { retryAfterSeconds },
      retryable: true,
    }),
  internal: (message = 'Lỗi hệ thống.', cause?: unknown): AppError =>
    new AppError('INTERNAL_ERROR', message, { cause }),
};

export function isAppError(e: unknown): e is AppError {
  return e instanceof AppError;
}

/** Never leak internals to the client; log the original separately. */
export function toPublicError(e: unknown): AppError {
  if (isAppError(e)) return e;
  return errors.internal('Lỗi hệ thống. Vui lòng thử lại.', e);
}
